import React, { useState } from 'react';

// Define types for our component
interface XmlToCsvProps {}

// XML parser that extracts data suitable for CSV conversion
const parseXmlForCsv = (xml: string): { headers: string[]; rows: Record<string, string>[] } => {
  try {
    // Create a DOM parser and parse the XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Find the root element
    const rootElement = xmlDoc.documentElement;
    
    // Determine the repeating elements (likely to be rows in CSV)
    const childElements = Array.from(rootElement.children);
    
    // If there are no child elements, throw an error
    if (childElements.length === 0) {
      throw new Error("No child elements found in XML. Cannot convert to CSV.");
    }
    
    // Get the most common element name to determine the row elements
    const elementCounts: Record<string, number> = {};
    let mostCommonElement = '';
    let maxCount = 0;
    
    childElements.forEach(child => {
      const tagName = child.tagName;
      elementCounts[tagName] = (elementCounts[tagName] || 0) + 1;
      
      if (elementCounts[tagName] > maxCount) {
        maxCount = elementCounts[tagName];
        mostCommonElement = tagName;
      }
    });
    
    // Get all elements that match the most common tag (these will be our rows)
    const rowElements = Array.from(rootElement.getElementsByTagName(mostCommonElement));
    
    if (rowElements.length === 0) {
      throw new Error("Could not identify repeating elements for CSV rows.");
    }
    
    // Collect all possible headers from all row elements
    const allHeaders = new Set<string>();
    const rows: Record<string, string>[] = [];
    
    // First pass: collect all possible headers
    rowElements.forEach(rowElement => {
      // Add simple element children
      Array.from(rowElement.children).forEach(child => {
        allHeaders.add(child.tagName);
      });
      
      // Add attributes
      Array.from(rowElement.attributes).forEach(attr => {
        allHeaders.add(`@${attr.name}`);
      });
    });
    
    // Second pass: fill data for each row
    rowElements.forEach(rowElement => {
      const rowData: Record<string, string> = {};
      
      // Process attributes
      Array.from(rowElement.attributes).forEach(attr => {
        rowData[`@${attr.name}`] = attr.value;
      });
      
      // Process child elements
      Array.from(rowElement.children).forEach(child => {
        // Handle simple text elements
        if (child.children.length === 0) {
          rowData[child.tagName] = child.textContent || '';
        } else {
          // For elements with children, we have a few options:
          // 1. Ignore them (complex nested structures are hard to represent in CSV)
          // 2. Serialize them to JSON
          // 3. Flatten them with dot notation
          
          // For this implementation, we'll serialize to a simple string representation
          rowData[child.tagName] = `[Complex: ${child.children.length} child elements]`;
          
          // Alternative: you could also try to extract nested data
          Array.from(child.children).forEach(grandchild => {
            if (grandchild.children.length === 0) {
              rowData[`${child.tagName}.${grandchild.tagName}`] = grandchild.textContent || '';
              allHeaders.add(`${child.tagName}.${grandchild.tagName}`);
            }
          });
        }
      });
      
      rows.push(rowData);
    });
    
    // Convert headers set to array
    const headers = Array.from(allHeaders);
    
    return { headers, rows };
  } catch (error) {
    throw error;
  }
};

// Convert parsed data to CSV string
const convertToCSV = (
  headers: string[], 
  rows: Record<string, string>[], 
  delimiter: string = ','
): string => {
  // Create the header row
  let csvContent = headers.map(header => `"${header.replace(/"/g, '""')}"`).join(delimiter) + '\n';
  
  // Add data rows
  rows.forEach(row => {
    const rowValues = headers.map(header => {
      const value = row[header] || '';
      // Quote values containing delimiter, quotes, or newlines
      if (value.includes(delimiter) || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    
    csvContent += rowValues.join(delimiter) + '\n';
  });
  
  return csvContent;
};

// Main component to convert XML to CSV
const XmlToCsv: React.FC<XmlToCsvProps> = () => {
  // State for the XML input
  const [xmlInput, setXmlInput] = useState<string>('');
  // State for the CSV output
  const [csvOutput, setCSVOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('xml_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isCsvGenerated, setIsCsvGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for CSV delimiter
  const [delimiter, setDelimiter] = useState<string>(',');
  // State for header inclusion option
  const [includeHeaders, setIncludeHeaders] = useState<boolean>(true);
  // State for handling nested data option
  const [nestedDataHandling, setNestedDataHandling] = useState<string>('flatten');
  // State for whether to include attributes
  const [includeAttributes, setIncludeAttributes] = useState<boolean>(true);

  // Handle XML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXmlInput(e.target.value);
    setError('');
    setIsCsvGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<library>
  <book id="bk101" category="fiction">
    <title>The Great Gatsby</title>
    <author>F. Scott Fitzgerald</author>
    <year>1925</year>
    <price>9.99</price>
    <tags>
      <tag>classic</tag>
      <tag>american literature</tag>
      <tag>jazz age</tag>
    </tags>
    <available>true</available>
  </book>
  <book id="bk102" category="non-fiction">
    <title>A Brief History of Time</title>
    <author>Stephen Hawking</author>
    <year>1988</year>
    <price>12.50</price>
    <tags>
      <tag>science</tag>
      <tag>physics</tag>
      <tag>cosmology</tag>
    </tags>
    <available>false</available>
  </book>
  <book id="bk103" category="fiction">
    <title>1984</title>
    <author>George Orwell</author>
    <year>1949</year>
    <price>8.75</price>
    <tags>
      <tag>dystopian</tag>
      <tag>political</tag>
    </tags>
    <available>true</available>
  </book>
</library>`;

    setXmlInput(exampleXml);
    setError('');
    setIsCsvGenerated(false);
    setCSVOutput('');
  };

  // Format/Beautify XML
  const formatXml = () => {
    try {
      if (!xmlInput.trim()) {
        setError('Please enter XML data to format');
        return;
      }
      
      // Parse the XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlInput, "application/xml");
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        throw new Error("XML Parsing Error: " + parseError[0].textContent);
      }
      
      // Serialize back to string with pretty formatting
      const serializer = new XMLSerializer();
      let formattedXml = '';
      
      // Function to pretty print XML with indentation
      const prettyPrintXml = (node: Node, indent: string = ''): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue?.trim();
          return text ? indent + text + '\n' : '';
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          let result = indent + '<' + element.nodeName;
          
          // Add attributes
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            result += ` ${attr.nodeName}="${attr.nodeValue}"`;
          }
          
          // Check if there are any child nodes
          if (element.childNodes.length === 0) {
            result += '/>\n';
            return result;
          }
          
          result += '>\n';
          
          // Process child nodes
          for (let i = 0; i < element.childNodes.length; i++) {
            result += prettyPrintXml(element.childNodes[i], indent + '  ');
          }
          
          result += indent + '</' + element.nodeName + '>\n';
          return result;
        }
        
        return '';
      };
      
      // Add XML declaration
      formattedXml += '<?xml version="1.0" encoding="UTF-8"?>\n';
      
      // Format the document element
      formattedXml += prettyPrintXml(xmlDoc.documentElement);
      
      setXmlInput(formattedXml);
      setError('');
    } catch (e) {
      setError(`Error formatting XML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert XML to CSV
  const convertXmlToCsv = () => {
    setError('');
    setIsLoading(true);
    setIsCsvGenerated(false);
    setCSVOutput('');
    
    if (!xmlInput.trim()) {
      setError('Please enter XML data');
      setIsLoading(false);
      return;
    }

    try {
      // Parse XML to extract data for CSV
      const { headers, rows } = parseXmlForCsv(xmlInput);
      
      // Filter headers based on user preferences
      let filteredHeaders = [...headers];
      
      // Remove attributes if not include
      if (!includeAttributes) {
        filteredHeaders = filteredHeaders.filter(header => !header.startsWith('@'));
      }
      
      // Filter nested data based on handling option
      if (nestedDataHandling === 'ignore') {
        filteredHeaders = filteredHeaders.filter(header => !header.includes('.'));
      }
      
      // Convert data to CSV string
      const csvString = convertToCSV(filteredHeaders, rows, delimiter);
      
      setCSVOutput(csvString);
      setIsCsvGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting XML to CSV: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setXmlInput('');
    setCSVOutput('');
    setError('');
    setFileName('xml_data');
    setDelimiter(',');
    setIncludeHeaders(true);
    setNestedDataHandling('flatten');
    setIncludeAttributes(true);
    setIsCsvGenerated(false);
  };

  // Copy CSV to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download CSV file
  const downloadCsv = () => {
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'xml_data'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">XML to CSV Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert XML data to CSV format. Paste your XML data below to generate a CSV file.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="xmlInput" className="font-medium text-gray-700">
                XML Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={formatXml}
                >
                  Format XML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setXmlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="xmlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={xmlInput}
              onChange={handleInputChange}
              placeholder="Paste your XML data here, e.g.,
<?xml version='1.0' encoding='UTF-8'?>
<library>
  <book id='1'>
    <title>Book Title</title>
    <author>Author Name</author>
    <year>2023</year>
  </book>
  <book id='2'>
    <title>Another Book</title>
    <author>Another Author</author>
    <year>2024</year>
  </book>
</library>"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    CSV Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeHeaders"
                    checked={includeHeaders}
                    onChange={(e) => setIncludeHeaders(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeHeaders" className="ml-2 block text-sm text-gray-700">
                    Include CSV Headers
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeAttributes"
                    checked={includeAttributes}
                    onChange={(e) => setIncludeAttributes(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeAttributes" className="ml-2 block text-sm text-gray-700">
                    Include XML Attributes
                  </label>
                </div>
                
                <div>
                  <label htmlFor="nestedDataHandling" className="block text-sm font-medium text-gray-700 mb-1">
                    Nested Data Handling
                  </label>
                  <select
                    id="nestedDataHandling"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={nestedDataHandling}
                    onChange={(e) => setNestedDataHandling(e.target.value)}
                  >
                    <option value="flatten">Flatten with Dot Notation</option>
                    <option value="ignore">Ignore Nested Elements</option>
                    <option value="serialize">Serialize as Text</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertXmlToCsv}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to CSV'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              CSV Output
            </label>
            
            {isCsvGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy CSV'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadCsv}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isCsvGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    CSV output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isCsvGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="csvOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={csvOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isCsvGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">XML Successfully Converted to CSV!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated CSV for use in your applications.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About XML to CSV Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms XML data into CSV format by identifying repeated elements as rows and their 
              children as columns. The converter automatically detects the structure of your XML and creates a 
              tabular representation, with options for handling attributes and nested elements.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Automatic Structure Detection</strong> - Identifies repeating elements to generate CSV rows</li>
              <li><strong>Customizable Delimiter</strong> - Choose between comma, semicolon, tab, or pipe delimiters</li>
              <li><strong>Attribute Handling</strong> - Option to include XML attributes as CSV columns</li>
              <li><strong>Nested Data Support</strong> - Multiple options for handling nested XML elements</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid XML input</li>
              <li><strong>One-Click Download</strong> - Save the converted CSV with a single click</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">XML to CSV Conversion Rules</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>XML elements with the same tag name are detected as rows in the CSV</li>
              <li>Child elements of the rows become columns in the CSV</li>
              <li>XML attributes can be included as columns with an "@" prefix</li>
              <li>Nested elements can be flattened using dot notation (e.g., "parent.child")</li>
              <li>Complex nested structures can be serialized or ignored based on settings</li>
              <li>CSV values are properly escaped and quoted when they contain special characters</li>
              <li>XML structures that can't be naturally represented in tabular format will be simplified</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Limitations</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Complex, deeply nested XML may not convert perfectly to the tabular CSV format</li>
              <li>XML with inconsistent structure may result in sparse CSV data (many empty cells)</li>
              <li>Arrays of values in XML are difficult to represent in CSV format</li>
              <li>Very large XML files may take longer to process in the browser</li>
              <li>XML comments and processing instructions are not included in the CSV output</li>
              <li>Special characters in XML may require proper escaping in the CSV output</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format XML" button to check if your XML is valid before conversion</li>
              <li>For large XML files, consider breaking them into smaller chunks</li>
              <li>XML with consistent structure converts best to CSV format</li>
              <li>Choose the appropriate nested data handling option for your data structure</li>
              <li>The "Use Example" button shows the expected XML format for optimal conversion</li>
              <li>More complex XML structures may require customization of the converter</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlToCsv;